import {Component, ElementRef, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {ImportadorIfoodService} from '../../../../services/importador-ifood.service';
import {CategoriaWizard, PlataformaImportacao, ProdutoWizard} from '../../wizard-state.model';

interface Plataforma {
  id: PlataformaImportacao;
  nome: string;
  logo: string;
  placeholder: string;
}

interface PreviewEmpresa {
  nome: string | null;
  logo: string | null;
  capa: string | null;
  endereco: string | null;
  cep: string | null;
  telefone: string | null;
  latitudeLongitude: string | null;
  horarios: any[] | null;
  totalProdutos: number;
  totalCategorias: number;
}

@Component({
  selector: 'app-step-importacao',
  templateUrl: './step-importacao.component.html',
  styleUrls: ['./step-importacao.component.scss']
})
export class StepImportacaoComponent {
  @Input() empresa: any = {};
  @ViewChild('linkInput') linkInput: ElementRef;
  @Output() onConcluido = new EventEmitter<{
    produtos: ProdutoWizard[];
    categorias: CategoriaWizard[];
    logo?: string;
    capa?: string;
    nomeEmpresa?: string | null;
    telefone?: string;
    enderecoImportado?: string;
    cep?: string;
    latitudeLongitude?: string;
    horarios?: any[];
  }>();
  @Output() onVoltar = new EventEmitter<void>();

  plataformaSelecionada: PlataformaImportacao | null = null;
  url = '';
  importando = false;
  erro: string | null = null;
  statusAdicionais: string | null = null;

  // Preview
  preview: PreviewEmpresa | null = null;
  dadosImportados: {produtos: ProdutoWizard[]; categorias: CategoriaWizard[]; resposta: any} | null = null;

  sistemas: Plataforma[] = [
    {id: 'anotaai', nome: 'Anota Aí', logo: 'assets/icons/logo-anotaai.png', placeholder: 'Cole o link da sua loja no Anota Aí'},
    {id: 'cardapioweb', nome: 'Cardápio Web', logo: 'assets/icons/logo-cardapioweb.png', placeholder: 'Cole o link da sua loja'},
    {id: 'cardapioai', nome: 'Cardapio.ai', logo: 'https://cardapio.ai/favicon.ico', placeholder: 'Cole o link da sua loja no Cardapio.ai'},
    {id: 'olaclick', nome: 'OlaClick', logo: 'assets/icons/logo-olaclick.png', placeholder: 'Cole o link da sua loja no OlaClick'},
    {id: 'goomer', nome: 'Goomer', logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRqxCSPYYW6j5QPSNa9lnLosO2VORnhRt0Lf3MvkZ2Kjw&s=10', placeholder: 'Cole o link da sua loja no Goomer'},
    {id: 'uairango', nome: 'UaiRango', logo: 'https://www.uairango.com/favicon.ico', placeholder: 'Cole o link da sua loja no UaiRango'},
    {id: 'aiqfome', nome: 'AiqFome', logo: 'assets/icons/logo-aiqfome.png', placeholder: 'Cole o link da sua loja no AiqFome'},
    {id: 'instadelivery', nome: 'InstaDelivery', logo: 'assets/icons/logo-instadelivery.jpg', placeholder: 'Cole o link da sua loja no InstaDelivery'},
    {id: 'menudino', nome: 'MenuDino', logo: 'assets/icons/logo-menudino.png', placeholder: 'Cole o link da sua loja no MenuDino'},
    {id: 'deliverydireto', nome: 'Delivery Direto', logo: 'https://deliverydireto.com.br/img/logo-delivery-direto-fundo-claro-1.png', placeholder: 'Cole o link da sua loja no Delivery Direto'},
    {id: 'takeat', nome: 'Takeat', logo: 'https://pedido.takeat.app/favicon.ico', placeholder: 'Cole o link da sua loja no Takeat'},
    {id: 'yooga', nome: 'Yooga', logo: 'assets/images/plataformas/yooga.png', placeholder: 'Cole o link da sua loja na Yooga'},
    {id: 'pedirdelivery', nome: 'Pedir Delivery', logo: 'https://multipedidos.com.br/favicon.png', placeholder: 'Cole o link da sua loja no Pedir Delivery'},
    {id: 'pecaqui', nome: 'Pecaqui', logo: 'https://pecaqui.online/img/iconheader.png', placeholder: 'Cole o link da sua loja no Pecaqui'},
    {id: 'livemenu', nome: 'Live Menu', logo: 'https://framerusercontent.com/images/pFV4wn9lFBLvchVwS1hwdLQFwEU.png', placeholder: 'Cole o link do cardápio no Live Menu'},
    {id: 'whatsmenu', nome: 'WhatsMenu', logo: 'assets/images/plataformas/whatsmenu.png', placeholder: 'Cole o link da sua loja no WhatsMenu'},
    {id: 'brendi', nome: 'Brendi', logo: 'assets/images/plataformas/brendi.webp', placeholder: 'Cole o link da sua loja no Brendi'},
    {id: 'meucardapio', nome: 'MeuCardápio.ai', logo: 'https://meucardapio.ai/favicon.ico', placeholder: 'Cole o link da sua loja no MeuCardápio.ai'}
  ];

  marketplaces: Plataforma[] = [
    {id: 'ifood', nome: 'iFood', logo: 'assets/icons/logo-ifood.png', placeholder: 'Cole o link da sua loja no iFood'}
  ];

  constructor(private importadorService: ImportadorIfoodService) {}

  selecionarPlataforma(plataforma: PlataformaImportacao) {
    this.plataformaSelecionada = plataforma;
    this.url = '';
    this.erro = null;
    this.preview = null;
    this.dadosImportados = null;
    setTimeout(() => this.linkInput?.nativeElement?.focus(), 0);
  }

  get placeholderAtual(): string {
    const p = [...this.sistemas, ...this.marketplaces].find(pl => pl.id === this.plataformaSelecionada);
    return p ? p.placeholder : '';
  }

  get podeImportar(): boolean {
    return !!this.plataformaSelecionada && !!this.url.trim();
  }

  importar() {
    if (!this.url.trim() || !this.plataformaSelecionada) return;

    this.importando = true;
    this.erro = null;
    this.preview = null;
    this.dadosImportados = null;

    let promise: Promise<any>;

    const salvarDados = true; // Wizard deve salvar dados da empresa (horários, endereço, etc.)

    switch (this.plataformaSelecionada) {
      case 'ifood':
        promise = this.importadorService.listeNovosProdutosIfood(this.url, -1, false, false, salvarDados);
        break;
      case 'anotaai':
        const slug = this.importadorService.extraiaSlugLojaAnota(this.url);
        promise = this.importadorService.listeNovosProdutosAnotaAi(slug || this.url, -1, false, false, salvarDados);
        break;
      case 'cardapioweb':
        promise = this.importadorService.listeNovosProdutosCardapioWeb(this.url, -1, false, false, salvarDados);
        break;
      case 'cardapioai':
        promise = this.importadorService.listeNovosProdutosCardapioAi(this.url, -1, false, false, salvarDados);
        break;
      case 'olaclick':
        promise = this.importadorService.listeNovosProdutosOlaclick(this.url, -1, false, false, salvarDados);
        break;
      case 'goomer':
        promise = this.importadorService.listeNovosProdutosGoomer(this.url, -1, false, false, salvarDados);
        break;
      case 'uairango':
        promise = this.importadorService.listeNovosProdutosUaiRango(this.url, -1, false, false, salvarDados);
        break;
      case 'aiqfome':
        promise = this.importadorService.listeNovosProdutosAiqFome(this.url, -1, false, false, salvarDados);
        break;
      case 'instadelivery':
        promise = this.importadorService.listeNovosProdutosInstaDelivery(this.url, -1, false, false, salvarDados);
        break;
      case 'menudino':
        promise = this.importadorService.listeNovosProdutosMenuDino(this.url, -1, false, false, salvarDados);
        break;
      case 'deliverydireto':
        promise = this.importadorService.listeNovosProdutosDeliveryDireto(this.url, -1, false, false, salvarDados);
        break;
      case 'takeat':
        promise = this.importadorService.listeNovosProdutosTakeat(this.url, -1, false, false, salvarDados);
        break;
      case 'yooga':
        promise = this.importadorService.listeNovosProdutosYooga(this.url, -1, false, false, salvarDados);
        break;
      case 'pedirdelivery':
        promise = this.importadorService.listeNovosProdutosPedirDelivery(this.url, -1, false, false, salvarDados);
        break;
      case 'pecaqui':
        promise = this.importadorService.listeNovosProdutosPecaqui(this.url, -1, false, false, salvarDados);
        break;
      case 'livemenu':
        promise = this.importadorService.listeNovosProdutosLiveMenu(this.url, -1, false, false, salvarDados);
        break;
      case 'whatsmenu':
        promise = this.importadorService.listeNovosProdutosWhatsMenu(this.url, -1, false, false, salvarDados);
        break;
      case 'brendi':
        promise = this.importadorService.listeNovosProdutosBrendi(this.url, -1, false, false, salvarDados);
        break;
      case 'meucardapio':
        promise = this.importadorService.listeNovosProdutosMeuCardapio(this.url, -1, false, false);
        break;
      default:
        return;
    }

    promise.then(async (resp: any) => {
      // No iFood (marketplace) os adicionais não vêm no catálogo: cada produto com
      // needChoices precisa de uma consulta própria, feita em páginas de 20 ids.
      if (this.plataformaSelecionada === 'ifood') {
        await this.carregueAdicionaisIfood(resp);
      }

      this.importando = false;
      this.statusAdicionais = null;
      const produtos = this.converterProdutos(resp);
      const categorias = this.agruparCategorias(produtos);
      this.dadosImportados = {produtos, categorias, resposta: resp};
      this.preview = this.montePreview(resp, produtos, categorias);
    }).catch((err: any) => {
      this.importando = false;
      this.statusAdicionais = null;
      this.erro = (err && err.message) || err || 'Não foi possível importar. Verifique o link e tente novamente.';
    });
  }

  private async carregueAdicionaisIfood(resp: any) {
    const produtos = this.extraiaProdutos(resp);
    const produtosComAdicionais = produtos.filter((p: any) => p && p.needChoices && p.idIfood);
    const ids: string[] = Array.from(new Set(produtosComAdicionais.map((p: any) => String(p.idIfood))));

    if (!ids.length) return;

    const tamanhoPagina = 20;
    const totalPaginas = Math.ceil(ids.length / tamanhoPagina);

    for (let i = 0; i < ids.length; i += tamanhoPagina) {
      const idsPagina = ids.slice(i, i + tamanhoPagina);
      const pagina = Math.floor(i / tamanhoPagina) + 1;

      this.statusAdicionais = `Buscando adicionais dos produtos (${pagina}/${totalPaginas})...`;

      const resposta = await this.busqueAdicionaisIfoodComRetentativa(idsPagina);

      (resposta?.produtos || []).forEach((produtoComAdicional: any) => {
        produtosComAdicionais
          .filter((p: any) => String(p.idIfood) === String(produtoComAdicional.idIfood))
          .forEach((p: any) => p.camposAdicionais = produtoComAdicional.camposAdicionais || []);
      });

      const naoEhUltimaPagina = (i + tamanhoPagina) < ids.length;
      if (naoEhUltimaPagina) await this.sleep(5000);
    }
  }

  private async busqueAdicionaisIfoodComRetentativa(ids: string[], tentativas = 3): Promise<any> {
    for (let tentativa = 1; tentativa <= tentativas; tentativa++) {
      try {
        return await this.importadorService.listeAdicionaisProdutosIfood(this.url, ids, -1);
      } catch (erro) {
        if (tentativa === tentativas) {
          throw new Error('Não foi possível buscar os adicionais dos produtos no iFood. Aguarde alguns minutos e tente novamente.');
        }
        this.statusAdicionais = 'O iFood demorou a responder, tentando de novo...';
        await this.sleep(5000);
      }
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  confirmarImportacao() {
    if (!this.dadosImportados) return;

    const {produtos, categorias, resposta} = this.dadosImportados;
    const identidadeVisual = this.obtenhaIdentidadeVisual(resposta);
    const extra = this.obtenhaEmpresaExtra(resposta);

    this.onConcluido.emit({
      produtos,
      categorias,
      nomeEmpresa: this.obtenhaNomeEmpresa(resposta),
      ...identidadeVisual,
      ...extra
    });
  }

  reimportar() {
    this.preview = null;
    this.dadosImportados = null;
    this.erro = null;
  }

  voltar() {
    if (this.preview) {
      this.reimportar();
      return;
    }
    this.onVoltar.emit();
  }

  formatarHorarios(horarios: any[]): string[] {
    if (!horarios?.length) return [];

    const diasNomes = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const porDia = new Map<number, string[]>();

    for (const h of horarios) {
      const dia = h.dayOfWeek ?? h.dia;
      if (dia == null) continue;
      const horario = `${h.open || h.inicio1 || ''} - ${h.close || h.fim1 || ''}`;
      if (!porDia.has(dia)) porDia.set(dia, []);
      porDia.get(dia).push(horario);
    }

    const resultado: string[] = [];
    for (let d = 0; d < 7; d++) {
      const faixas = porDia.get(d);
      if (faixas?.length) {
        resultado.push(`${diasNomes[d]}: ${faixas.join(', ')}`);
      }
    }

    return resultado;
  }

  private montePreview(resp: any, produtos: ProdutoWizard[], categorias: CategoriaWizard[]): PreviewEmpresa {
    const info = resp?.empresaInfo || {};
    return {
      nome: info.nome || null,
      logo: info.logo || info.imagem || null,
      capa: info.capa || info.banner || null,
      endereco: info.endereco || null,
      cep: info.cep || null,
      telefone: info.whatsapp || info.telefone || null,
      latitudeLongitude: info.latitudeLongitude || null,
      horarios: info.horarios || null,
      totalProdutos: produtos.length,
      totalCategorias: categorias.length
    };
  }

  private converterProdutos(resp: any): ProdutoWizard[] {
    if (!resp) return [];

    const produtos = this.extraiaProdutos(resp);

    return produtos.map((p: any) => ({
      id: p.id || null,
      nome: p.nome || p.name || '',
      descricao: p.descricao || p.description || '',
      preco: p.preco || p.price || 0,
      imagem: p.imagem || p.image || p.linkImagem || null,
      categoria: this.normalizarCategoria(p),
      selecionado: true,
      salvo: !!p.id,
      exemplo: false,
      adicionais: p.adicionais || p.camposAdicionais || [],
      payload: p
    }));
  }

  private extraiaProdutos(resp: any): any[] {
    if (Array.isArray(resp)) return resp;
    if (Array.isArray(resp.novosProdutos)) return resp.novosProdutos;
    if (Array.isArray(resp.novos)) return resp.novos;
    if (Array.isArray(resp.produtos)) return resp.produtos;
    return [];
  }

  private normalizarCategoria(produto: any): string {
    const categoria = produto?.categoria;

    if (typeof categoria === 'string' && categoria.trim()) {
      return categoria;
    }

    if (categoria?.nome) {
      return categoria.nome;
    }

    if (produto?.nomeCategoria) {
      return produto.nomeCategoria;
    }

    return 'Importados';
  }

  private obtenhaIdentidadeVisual(resp: any): {logo?: string; capa?: string} {
    const empresaInfo = resp?.empresaInfo || {};

    return {
      logo: empresaInfo.logo || empresaInfo.imagem || undefined,
      capa: empresaInfo.capa || empresaInfo.banner || undefined
    };
  }

  private obtenhaNomeEmpresa(resp: any): string | null {
    return resp?.empresaInfo?.nome || null;
  }

  private obtenhaEmpresaExtra(resp: any): {telefone?: string; enderecoImportado?: string; cep?: string; latitudeLongitude?: string; horarios?: any[]} {
    const info = resp?.empresaInfo || {};
    return {
      telefone: info.whatsapp || undefined,
      enderecoImportado: info.endereco || undefined,
      cep: info.cep || undefined,
      latitudeLongitude: info.latitudeLongitude || undefined,
      horarios: info.horarios?.length ? info.horarios : undefined
    };
  }

  private agruparCategorias(produtos: ProdutoWizard[]): CategoriaWizard[] {
    const map = new Map<string, ProdutoWizard[]>();
    produtos.forEach(p => {
      if (!map.has(p.categoria)) map.set(p.categoria, []);
      map.get(p.categoria).push(p);
    });
    return Array.from(map.entries()).map(([nome, prods]) => ({nome, produtos: prods}));
  }
}
